import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import { downloadTableAsExcel } from '@/helpers/PrintHelper';
import { DELIVERY_ADDRESS_WEEK_DAYS } from '@/modules/Logistics/composables/LogisticsTableHelper';
import { AddressRow } from '@/modules/Logistics/interfaces/LogisticsInterfaces';
import DeliveryRegion from '@/models/DeliveryRegion';
import i18n from '@/i18n';
import router from '@/router';
import { RouteNames } from '@/enums/routes/RouteNames';

interface LogisticsAddressesProps {
    dataSource?: AddressRow[];
    loading?: boolean;
    searchQuery?: string;
    selectedDays?: string[];
    selectedDeliveryRegion?: string;
    deliveryRegions?: DeliveryRegion[];
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LogisticsAddresses',
  props: {
    dataSource: { default: () => [] },
    loading: { type: Boolean, default: false },
    searchQuery: { default: '' },
    selectedDays: { default: () => [] },
    selectedDeliveryRegion: { default: undefined },
    deliveryRegions: { default: () => [] }
  },
  emits: ["update:searchQuery", "update:selectedDays", "update:selectedDeliveryRegion", "fetchAddressesInitialData"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'update:searchQuery', value: string): void;
    (e: 'update:selectedDays', days: string[]): void;
    (e: 'update:selectedDeliveryRegion', deliveryRegion: string | null): void;
    (e: 'fetchAddressesInitialData'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { dataSource: AddressRow[], loading: boolean, searchQuery: string, selectedDays: string[], selectedDeliveryRegion: string, deliveryRegions: DeliveryRegion[] };



// Emits


const sortState = ref<{ columnKey: keyof AddressRow | null; order: 'ascend' | 'descend' | null }>({
    columnKey: null,
    order: null,
});

const columns = [
    {
        title: i18n.t('Client'),
        dataIndex: 'clientName',
        key: 'clientName',
        sorter: (a: AddressRow, b: AddressRow) => a.clientName.localeCompare(b.clientName),
    },
    {
        title: i18n.t('Address'),
        dataIndex: 'address',
        key: 'address',
        sorter: (a: AddressRow, b: AddressRow) => a.address.localeCompare(b.address),
    },
    {
        title: i18n.t('Contact name'),
        dataIndex: 'contactName',
        key: 'contactName',
        sorter: (a: AddressRow, b: AddressRow) => a.contactName.localeCompare(b.contactName),
    },
    {
        title: i18n.t('Contact email'),
        dataIndex: 'contactEmail',
        key: 'contactEmail',
        sorter: (a: AddressRow, b: AddressRow) => a.contactEmail.localeCompare(b.contactEmail),
    },
    {
        title: i18n.t('Contact phone'),
        dataIndex: 'contactPhone',
        key: 'contactPhone',
        sorter: (a: AddressRow, b: AddressRow) => a.contactPhone.localeCompare(b.contactPhone),
    },
    {
        title: i18n.t('Comments'),
        dataIndex: 'comments',
        key: 'comments',
        sorter: (a: AddressRow, b: AddressRow) => a.comments.localeCompare(b.comments),
    },
];

// Computed
const filteredData = computed(() => {
    if (!props.searchQuery) {
        return props.dataSource;
    }
    const lowerQuery = props.searchQuery.toLowerCase();
    return props.dataSource.filter((item) =>
        [item.clientName, item.address, item.contactName, item.contactEmail, item.contactPhone, item.comments]
            .join(' ')
            .toLowerCase()
            .includes(lowerQuery),
    );
});

const sortedFilteredData = computed(() => {
    if (!sortState.value.columnKey || !sortState.value.order) {
        return filteredData.value;
    }

    const sorted = [...filteredData.value].sort((leftItem, rightItem) => {
        const leftValue = String(leftItem[sortState.value.columnKey!] ?? '');
        const rightValue = String(rightItem[sortState.value.columnKey!] ?? '');

        return leftValue.localeCompare(rightValue);
    });

    return sortState.value.order === 'ascend' ? sorted : sorted.reverse();
});

// Methods
(() => {
    emit('fetchAddressesInitialData');
})();

function handleTableChange(_pagination: unknown, _filters: unknown, sorter: any) {
    sortState.value = {
        columnKey: (sorter?.columnKey as keyof AddressRow) ?? null,
        order: sorter?.order ?? null,
    };
}

function toggleDay(dayKey: string) {
    const nextSelectedDays = props.selectedDays.includes(dayKey)
        ? props.selectedDays.filter((day) => day !== dayKey)
        : [...props.selectedDays, dayKey];

    emit('update:selectedDays', nextSelectedDays);
}

function handleSearchChange(e: any) {
    emit('update:searchQuery', e.target.value);
}

function handleDeliveryRegionChange(value: string | undefined) {
    emit('update:selectedDeliveryRegion', value || null);
}

function getRowKey(_record: AddressRow, index?: number) {
    return index ?? 0;
}

function onRowClick(selectedRow: any) {
    return {
        on: {
            click: () => {
                if (!selectedRow.clientId) {
                    return;
                }
                // Router switch
                const route = router.resolve({
                    name: RouteNames.editClient,
                    params: { clientId: selectedRow.clientId },
                });
                window.open(route.href, '_blank');
            },
        },
        style: {
            cursor: selectedRow.clientId ? 'pointer' : 'default',
        },
    };
}

function handleDownloadExcel() {
    downloadTableAsExcel(columns, sortedFilteredData.value, 'delivery-addresses');
}

return { __sfc: true,props, emit, sortState, columns, filteredData, sortedFilteredData, handleTableChange, toggleDay, handleSearchChange, handleDeliveryRegionChange, getRowKey, onRowClick, handleDownloadExcel, DELIVERY_ADDRESS_WEEK_DAYS }
}

})